import axios from 'axios';
import axiosRetry from 'axios-retry';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { SocketConnector } from '@/contexts/socket';
import { setRecaptchaModal } from '@/contexts/event.const';
import AuthStorage from '@/services/auth/storage';
import { TokenTypeEnum } from '@/services/auth/storage/types';
import EventBus from './event-handler';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || '';
export const customerAppUrl = process.env.NEXT_PUBLIC_CUSTOMER_APP_URL || '';
export const gaToken: string = process.env.NEXT_PUBLIC_GA_TOKEN || '';
export const isFirstLoginKey = 'isFirstLogin';
export const notificationSettingsKey = 'notificationSettings';
export const tableViewFilterKey = 'tableViewFilterV2';
export const restaurantDataKey = 'restaurantData';
export const loyaltyProgramDataKey = 'loyaltyProgramData';
export const redirectToPaymentLink = 'redirectToPaymentLink';
export const selectedBrandOrRestaurantKey = 'selectedBrandOrRestaurant';

export const clearLocalStorage = () => {
    AuthStorage.removeAccessToken();
    AuthStorage.removeRefreshToken();
    AuthStorage.removeCognitoToken();
    AuthStorage.removeTokenType();
    AuthStorage.removeRestaurantId();
    AuthStorage.removePosVendor();
    AuthStorage.removeUsername();
    AuthStorage.removeUserRole();
    AuthStorage.removeUserId();
    AuthStorage.removeCountryCode();
    AuthStorage.removeTimezone();
    localStorage.removeItem(notificationSettingsKey);
    localStorage.removeItem(tableViewFilterKey);
    localStorage.removeItem(restaurantDataKey);
    localStorage.removeItem(loyaltyProgramDataKey);
    localStorage.removeItem(redirectToPaymentLink);
    localStorage.removeItem(selectedBrandOrRestaurantKey);
};

let cancelTokenSource = axios.CancelToken.source();

const axiosInstance = axios.create({
    baseURL: baseUrl,
    headers: {
        Accepted: 'application/json',
        'Content-Type': 'application/json',
    },
});

axiosRetry(axiosInstance, {
    retries: 3,
    retryDelay: (retryCount) => 1000 * retryCount,
    retryCondition: (error) => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNABORTED';
    },
});

axiosInstance.interceptors.request.use((config) => {
    const token = AuthStorage.getAccessToken();
    const type = AuthStorage.getTokenType();
    if (token && type && config.headers) {
        config.headers.authorization = `Bearer ${token}`;
        config.headers['x-token-type'] = type;
    }

    const userId = AuthStorage.getUserId();
    if (config.headers && userId) {
        config.headers['X-Amzn-Trace-Id'] = `UserID:${userId}`;
    }

    config.cancelToken = cancelTokenSource.token;
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && error.response.status === 429) {
            // Cancel all pending requests
            cancelTokenSource.cancel('Too many requests');

            // Create new cancel token source for future requests
            cancelTokenSource = axios.CancelToken.source();

            // Open the recaptcha modal
            EventBus.dispatch(setRecaptchaModal);

            // You might want to add a delay before allowing new requests
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(axios(error.config));
                }, 5000);
            });
        }
        return Promise.reject(error);
    },
);

const refreshAuthLogic = (failedRequest: any) => {
    const refreshToken = AuthStorage.getRefreshToken();
    const type = AuthStorage.getTokenType();
    if (!type || !refreshToken) {
        clearLocalStorage();
        return Promise.reject(new Error('Invalid type'));
    }

    return axiosInstance
        .post(`/auth/refresh`, { refreshToken, type })
        .then((tokenRefreshResponse) => {
            AuthStorage.setAccessToken(tokenRefreshResponse.data?.data.token || '');
            AuthStorage.setCognitoToken(tokenRefreshResponse.data?.data.access_token || '');
            const restaurantId = AuthStorage.getRestaurantId();
            // connect the socket with new access token
            if (tokenRefreshResponse.data?.data.token && restaurantId) {
                SocketConnector.getInstance().destroy();
                const tokenType = AuthStorage.getTokenType();
                SocketConnector.getInstance()
                    .modify(
                        {
                            accessToken: tokenRefreshResponse.data?.data.token,
                            restaurantId,
                            tokenType: tokenType || TokenTypeEnum.Vendor,
                        },
                        true,
                    )
                    .catch(() => {
                        console.log('Error while connecting with socket with new token');
                    });
            }
            failedRequest.response.config.headers.authorization = `Bearer ${tokenRefreshResponse.data}`;
            return Promise.resolve();
        })
        .catch((err) => {
            clearLocalStorage();
            window.location.reload();
            return Promise.reject(err);
        });
};

// Instantiate the interceptor
createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic, {
    statusCodes: [401],
});

export default axiosInstance;
